import { z } from "zod"

import type {
	NotificationCategory,
	NotificationContent,
	NotificationEntityType,
	NotificationScope,
	NotificationSubscriptionSource,
} from "./constants"
import { notificationTitles as t } from "./copy"

/** Loose on purpose: lib can't import zero's threadFiltersSchema; the server mutator validates it. */
export type NotificationFilter = Record<string, unknown>

export interface NotificationRenderContext {
	actorName: string | null
	thread: {
		id: string
		title: string | null
		contactName: string | null
		companyName: string | null
	} | null
}

export interface NotificationTypeDefinition<
	S extends z.ZodTypeAny = z.ZodTypeAny,
> {
	category: NotificationCategory
	entityType: NotificationEntityType
	payload: S
	/** Inbox scopes a member may pick. "all" only where a workspace-wide stream makes sense. */
	scopes: readonly NotificationScope[]
	defaults: {
		inbox: NotificationScope
		desktop: boolean
		filter?: NotificationFilter | null
	}
	/** Who counts as involved besides direct targets. Topics "emitted" = only the topic named on the event. */
	involves: {
		assignee: boolean
		entitySubscribers: boolean
		topics: boolean | "emitted"
	}
	audience?: "members" | "admins"
	/** Thread types are dropped once the thread is done; these still matter after it closes. */
	keepWhenThreadDone?: boolean
	autoSubscribe?: {
		actor?: NotificationSubscriptionSource
		directTargets?: NotificationSubscriptionSource
	}
	aliases?: readonly string[]
	// Method syntax keeps the payload parameter bivariant, so typed entries fit the generic registry type.
	render(
		payload: z.infer<S>,
		ctx: NotificationRenderContext,
	): NotificationContent
}

function defineType<S extends z.ZodTypeAny>(
	def: NotificationTypeDefinition<S>,
): NotificationTypeDefinition<S> {
	return def
}

const THREAD_INVOLVES = {
	assignee: true,
	entitySubscribers: true,
	topics: true,
} as const
const DIRECT_ONLY = {
	assignee: false,
	entitySubscribers: false,
	topics: false,
} as const
const ASSIGNEE_ONLY = {
	assignee: true,
	entitySubscribers: false,
	topics: false,
} as const
const ENTITY_SUBSCRIBERS = {
	assignee: false,
	entitySubscribers: true,
	topics: false,
} as const

const ALL_SCOPES = ["off", "involved", "all"] as const
const INVOLVED_SCOPES = ["off", "involved"] as const
const UNASSIGNED: NotificationFilter = { assigneeIds: [null] }

const previewSchema = z.string().max(400).nullish()

function threadTarget(ctx: NotificationRenderContext) {
	return ctx.thread
		? ({ kind: "thread", threadId: ctx.thread.id } as const)
		: ({ kind: "none" } as const)
}

function threadTitle(ctx: NotificationRenderContext) {
	return ctx.thread?.title?.trim() || t.untitledThread
}

function customerName(ctx: NotificationRenderContext) {
	return (
		ctx.thread?.contactName ?? ctx.thread?.companyName ?? t.fallbackCustomer
	)
}

/** Workspace-level types whose title and body come from the caller (existing email copy). */
const prerenderedSchema = z.object({
	title: z.string(),
	body: z.string().optional(),
	settingsPath: z.string().optional(),
})

function prerendered(
	p: z.infer<typeof prerenderedSchema>,
): NotificationContent {
	return {
		title: p.title,
		body: p.body,
		target: p.settingsPath
			? { kind: "settings", path: p.settingsPath }
			: { kind: "none" },
	}
}

export const NOTIFICATION_TYPES = {
	"thread.created": defineType({
		category: "threads",
		entityType: "thread",
		payload: z.object({ preview: previewSchema }),
		scopes: ALL_SCOPES,
		defaults: { inbox: "all", desktop: false },
		involves: THREAD_INVOLVES,
		autoSubscribe: { actor: "creator" },
		render: (p, ctx) => ({
			title: t.threadCreated(customerName(ctx)),
			// The title is still the "New thread" placeholder when this fires.
			body: p.preview ?? threadTitle(ctx),
			target: threadTarget(ctx),
		}),
	}),
	"thread.reply_received": defineType({
		category: "threads",
		entityType: "thread",
		payload: z.object({
			messageId: z.string(),
			preview: previewSchema,
			/** Live chat already pops its own browser notification. */
			liveChat: z.boolean().optional(),
		}),
		scopes: ALL_SCOPES,
		defaults: { inbox: "involved", desktop: true },
		involves: THREAD_INVOLVES,
		render: (p, ctx) => ({
			title: t.replyReceived(customerName(ctx)),
			body: p.preview ?? threadTitle(ctx),
			target: threadTarget(ctx),
		}),
	}),
	"thread.reaction_received": defineType({
		category: "threads",
		entityType: "thread",
		payload: z.object({
			messageId: z.string(),
			/** The glyph, or ":name:" for a custom emoji. */
			emoji: z.string().max(120),
			reactorName: z.string().max(200).nullish(),
		}),
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: true },
		involves: ASSIGNEE_ONLY,
		// Customers often react once the thread is closed, to say thanks.
		keepWhenThreadDone: true,
		render: (p, ctx) => ({
			title: t.reactionReceived(p.reactorName ?? customerName(ctx), p.emoji),
			body: threadTitle(ctx),
			target: threadTarget(ctx),
		}),
	}),
	"thread.assigned": defineType({
		category: "threads",
		entityType: "thread",
		payload: z.object({}),
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: true },
		involves: DIRECT_ONLY,
		autoSubscribe: { directTargets: "assignee" },
		render: (_p, ctx) => ({
			title: t.assigned(
				ctx.actorName,
				ctx.thread?.companyName ?? ctx.thread?.contactName ?? null,
			),
			body: threadTitle(ctx),
			target: threadTarget(ctx),
		}),
	}),
	"thread.unassigned": defineType({
		category: "threads",
		entityType: "thread",
		payload: z.object({}),
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: DIRECT_ONLY,
		render: (_p, ctx) => ({
			title: t.unassigned(ctx.actorName),
			body: threadTitle(ctx),
			target: threadTarget(ctx),
		}),
	}),
	"thread.done": defineType({
		category: "threads",
		entityType: "thread",
		payload: z.object({ source: z.string().optional() }),
		scopes: ALL_SCOPES,
		// Off by default: marking a thread done clears its notifications, so this one would be the only leftover.
		defaults: { inbox: "off", desktop: false },
		involves: THREAD_INVOLVES,
		keepWhenThreadDone: true,
		render: (_p, ctx) => ({
			title: t.done(ctx.actorName),
			body: threadTitle(ctx),
			target: threadTarget(ctx),
		}),
	}),
	"thread.reopened": defineType({
		category: "threads",
		entityType: "thread",
		payload: z.object({ source: z.string().optional() }),
		scopes: ALL_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: THREAD_INVOLVES,
		render: (_p, ctx) => ({
			title: t.reopened(),
			body: threadTitle(ctx),
			target: threadTarget(ctx),
		}),
	}),
	"thread.ai_draft_ready": defineType({
		category: "threads",
		entityType: "thread",
		payload: z.object({ preview: previewSchema }),
		scopes: ALL_SCOPES,
		// Off by default: drafts show in the thread itself. Switching to All
		// still starts from the unassigned filter.
		defaults: { inbox: "off", desktop: false, filter: UNASSIGNED },
		involves: THREAD_INVOLVES,
		render: (p, ctx) => ({
			title: t.aiDraftReady(),
			body: threadTitle(ctx) + (p.preview ? `: ${p.preview}` : ""),
			target: threadTarget(ctx),
		}),
	}),
	"thread.ai_handoff": defineType({
		category: "threads",
		entityType: "thread",
		payload: z.object({ reason: z.string().optional() }),
		scopes: ALL_SCOPES,
		defaults: { inbox: "all", desktop: true },
		involves: THREAD_INVOLVES,
		render: (_p, ctx) => ({
			title: t.aiHandoff(),
			body: threadTitle(ctx),
			target: threadTarget(ctx),
		}),
	}),
	"thread.ai_resolved": defineType({
		category: "threads",
		entityType: "thread",
		payload: z.object({ reason: z.string().optional() }),
		scopes: ALL_SCOPES,
		defaults: { inbox: "all", desktop: false },
		involves: THREAD_INVOLVES,
		// The close itself is the news, so marking it done must not clear it.
		keepWhenThreadDone: true,
		render: (_p, ctx) => ({
			title: t.aiResolved(),
			body: threadTitle(ctx),
			target: threadTarget(ctx),
		}),
	}),
	"thread.snooze_ended": defineType({
		category: "threads",
		entityType: "thread",
		payload: z.object({}),
		scopes: ALL_SCOPES,
		// The assignee hears about their own thread; an unassigned one goes to the whole team.
		defaults: { inbox: "all", desktop: true, filter: UNASSIGNED },
		involves: ASSIGNEE_ONLY,
		render: (_p, ctx) => ({
			title: t.snoozeEnded(),
			body: threadTitle(ctx),
			target: threadTarget(ctx),
		}),
	}),
	"thread.sla_at_risk": defineType({
		category: "threads",
		entityType: "thread",
		payload: z.object({ deadlineAt: z.string() }),
		scopes: ALL_SCOPES,
		defaults: { inbox: "all", desktop: true, filter: UNASSIGNED },
		involves: THREAD_INVOLVES,
		render: (_p, ctx) => ({
			title: t.slaAtRisk(),
			body: threadTitle(ctx),
			target: threadTarget(ctx),
		}),
	}),
	"thread.sla_breached": defineType({
		category: "threads",
		entityType: "thread",
		payload: z.object({ deadlineAt: z.string() }),
		scopes: ALL_SCOPES,
		defaults: { inbox: "all", desktop: true, filter: UNASSIGNED },
		involves: THREAD_INVOLVES,
		render: (_p, ctx) => ({
			title: t.slaBreached(),
			body: threadTitle(ctx),
			target: threadTarget(ctx),
		}),
	}),
	"thread.topic_added": defineType({
		category: "threads",
		entityType: "thread",
		payload: z.object({
			topicType: z.enum(["tag", "company"]),
			topicName: z.string().optional(),
		}),
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: { assignee: false, entitySubscribers: false, topics: "emitted" },
		render: (p, ctx) => ({
			title: t.topicAdded(p.topicName ?? p.topicType),
			body: threadTitle(ctx),
			target: threadTarget(ctx),
		}),
	}),
	"thread.linked_issue_completed": defineType({
		category: "threads",
		entityType: "thread",
		payload: z.object({
			issueId: z.string(),
			issueIdentifier: z.string().optional(),
		}),
		scopes: ALL_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: THREAD_INVOLVES,
		render: (p, ctx) => ({
			title: t.linkedIssueCompleted(p.issueIdentifier ?? threadTitle(ctx)),
			body: threadTitle(ctx),
			target: threadTarget(ctx),
		}),
	}),
	"comment.mentioned": defineType({
		category: "comments",
		entityType: "thread",
		payload: z.object({ commentId: z.string(), preview: previewSchema }),
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: true },
		involves: DIRECT_ONLY,
		autoSubscribe: { directTargets: "mentioned" },
		keepWhenThreadDone: true,
		render: (p, ctx) => ({
			title: t.mentioned(ctx.actorName),
			body: p.preview ?? threadTitle(ctx),
			target: threadTarget(ctx),
		}),
	}),
	"comment.created": defineType({
		category: "comments",
		entityType: "thread",
		payload: z.object({ commentId: z.string(), preview: previewSchema }),
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: { assignee: true, entitySubscribers: true, topics: false },
		autoSubscribe: { actor: "commented" },
		keepWhenThreadDone: true,
		render: (p, ctx) => ({
			title: t.commented(ctx.actorName),
			body: p.preview ?? threadTitle(ctx),
			target: threadTarget(ctx),
		}),
	}),
	"issue.request_linked": defineType({
		category: "subscriptions",
		entityType: "issue",
		payload: z.object({
			issueId: z.string(),
			issueIdentifier: z.string().optional(),
			threadId: z.string(),
		}),
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: ENTITY_SUBSCRIBERS,
		keepWhenThreadDone: true,
		render: (p, ctx) => ({
			title: t.requestLinked(p.issueIdentifier ?? threadTitle(ctx)),
			body: threadTitle(ctx),
			target: { kind: "thread", threadId: p.threadId },
		}),
	}),
	"issue.status_changed": defineType({
		category: "subscriptions",
		entityType: "issue",
		payload: z.object({
			issueId: z.string(),
			issueIdentifier: z.string(),
			statusName: z.string(),
		}),
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: ENTITY_SUBSCRIBERS,
		render: (p) => ({
			title: t.statusChanged(p.issueIdentifier, p.statusName),
			target: { kind: "issue", issueId: p.issueId },
		}),
	}),
	"project.request_linked": defineType({
		category: "subscriptions",
		entityType: "project",
		payload: z.object({
			projectId: z.string(),
			projectName: z.string().optional(),
			threadId: z.string(),
		}),
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: ENTITY_SUBSCRIBERS,
		keepWhenThreadDone: true,
		render: (p, ctx) => ({
			title: t.requestLinked(p.projectName ?? threadTitle(ctx)),
			body: threadTitle(ctx),
			target: { kind: "thread", threadId: p.threadId },
		}),
	}),
	"project.status_changed": defineType({
		category: "subscriptions",
		entityType: "project",
		payload: z.object({
			projectId: z.string(),
			projectName: z.string(),
			statusName: z.string(),
		}),
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: ENTITY_SUBSCRIBERS,
		render: (p) => ({
			title: t.statusChanged(p.projectName, p.statusName),
			target: { kind: "project", projectId: p.projectId },
		}),
	}),
	"docs.proposal_ready": defineType({
		category: "subscriptions",
		entityType: "docsArticle",
		payload: z.object({
			articleId: z.string().nullable(),
			articleTitle: z.string().nullable().optional(),
		}),
		scopes: ALL_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: ENTITY_SUBSCRIBERS,
		render: (p) => ({
			title: t.docsProposalReady(p.articleTitle ?? null),
			target: p.articleId
				? { kind: "docsArticle", articleId: p.articleId }
				: { kind: "docsProposals" },
		}),
	}),
	"docs.api_sync_held": defineType({
		category: "workspace",
		entityType: "workspace",
		payload: prerenderedSchema,
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: DIRECT_ONLY,
		audience: "admins",
		render: prerendered,
	}),
	"docs.api_sync_failed": defineType({
		category: "workspace",
		entityType: "workspace",
		payload: prerenderedSchema,
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: DIRECT_ONLY,
		audience: "admins",
		render: prerendered,
	}),
	"billing.payment_failed": defineType({
		category: "workspace",
		entityType: "workspace",
		payload: prerenderedSchema,
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: DIRECT_ONLY,
		audience: "admins",
		render: prerendered,
	}),
	"ai.cap_reached": defineType({
		category: "workspace",
		entityType: "workspace",
		payload: prerenderedSchema,
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: DIRECT_ONLY,
		audience: "admins",
		render: prerendered,
	}),
	"slack.broadcast_disconnected": defineType({
		category: "workspace",
		entityType: "workspace",
		payload: z.object({}),
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: DIRECT_ONLY,
		audience: "admins",
		render: () => ({
			title: t.slackBroadcastDisconnected(),
			body: t.slackBroadcastDisconnectedBody(),
			target: { kind: "settings", path: "/settings/integrations/slack" },
		}),
	}),
	"slack.personal_dm_failed": defineType({
		category: "workspace",
		entityType: "workspace",
		payload: z.object({}),
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: DIRECT_ONLY,
		render: () => ({
			title: t.slackPersonalDmFailed(),
			target: { kind: "settings", path: "/settings/notifications/slack" },
		}),
	}),
	"import.zendesk_finished": defineType({
		category: "workspace",
		entityType: "workspace",
		payload: z.object({}),
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: DIRECT_ONLY,
		render: () => ({ title: t.zendeskFinished(), target: { kind: "none" } }),
	}),
	"import.zendesk_failed": defineType({
		category: "workspace",
		entityType: "workspace",
		payload: z.object({}),
		scopes: INVOLVED_SCOPES,
		defaults: { inbox: "involved", desktop: false },
		involves: DIRECT_ONLY,
		render: () => ({ title: t.zendeskFailed(), target: { kind: "none" } }),
	}),
} satisfies Record<string, NotificationTypeDefinition>

export type NotificationType = keyof typeof NOTIFICATION_TYPES

export type NotificationPayloadMap = {
	[K in NotificationType]: z.infer<(typeof NOTIFICATION_TYPES)[K]["payload"]>
}

const aliasIndex = new Map<string, NotificationType>()
for (const [key, def] of Object.entries(NOTIFICATION_TYPES)) {
	const aliases = (def as NotificationTypeDefinition).aliases ?? []
	for (const alias of aliases) aliasIndex.set(alias, key as NotificationType)
}

export function isNotificationType(type: string): type is NotificationType {
	return Object.prototype.hasOwnProperty.call(NOTIFICATION_TYPES, type)
}

/** Resolves renamed types to their current key; undefined for removed or unknown types. */
export function resolveNotificationType(
	type: string,
): NotificationType | undefined {
	if (isNotificationType(type)) return type
	return aliasIndex.get(type)
}

export function getNotificationTypeDefinition(
	type: string,
): NotificationTypeDefinition | undefined {
	const key = resolveNotificationType(type)
	return key
		? (NOTIFICATION_TYPES[key] as NotificationTypeDefinition)
		: undefined
}

export function notificationTypeKeys(): NotificationType[] {
	return Object.keys(NOTIFICATION_TYPES) as NotificationType[]
}
